import { useCallback, useEffect, useRef, useState } from 'react';
import { peopleGateway } from '../../index';
import { effectiveStatusOf } from '../../reciprocity';
import { stateName } from '../../stateNames';
import type { ReciprocityRequestAdminItem } from '../../types';
import { TaxTermHelp } from '../../../tax-settings/TaxTermHelp';

/** The words for a request's state, used in its accessible name. Approved stays "Approved" unless the approval has expired. */
function chipLabel(item: ReciprocityRequestAdminItem): string {
  switch (item.status) {
    case 'SubmittedPendingReview':
      return 'Pending review';
    case 'Approved':
      return effectiveStatusOf(item) === 'Expired' ? 'Expired' : 'Approved';
    case 'Rejected':
      return 'Rejected';
    case 'Withdrawn':
      return 'Withdrawn by employee';
    case 'Revoked':
      return 'Revoked';
  }
}

const MAX_NOTE = 500;

const formatDate = (value: string | null | undefined) => {
  if (!value) return '';
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
  return m ? `${m[2]}/${m[3]}/${m[1]}` : value;
};
const formatSize = (bytes: number) => (bytes >= 1024 * 1024 ? `${(bytes / (1024 * 1024)).toFixed(1)} MB` : `${Math.max(1, Math.round(bytes / 1024))} KB`);

type Action = 'Approve' | 'Reject' | 'Revoke';

interface CardProps {
  employeeId: string;
  item: ReciprocityRequestAdminItem;
  /** A decision returns the updated request; a revocation returns nothing, so the list is read again (`updated` is undefined). */
  onChanged: (message: string, updated?: ReciprocityRequestAdminItem) => void;
}

function RequestCard({ employeeId, item, onChanged }: CardProps) {
  const effective = effectiveStatusOf(item);
  const pending = item.status === 'SubmittedPendingReview';
  const canDecide = pending && !item.isStale;
  const canRevoke = item.status === 'Approved' && effective !== 'Expired';
  const [decision, setDecision] = useState<'Approve' | 'Reject' | ''>('');
  const [revoking, setRevoking] = useState(false);
  const [effectiveDate, setEffectiveDate] = useState('');
  const [expiresOn, setExpiresOn] = useState('');
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [confirming, setConfirming] = useState<Action | null>(null);
  const [saving, setSaving] = useState(false);
  const confirmButton = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (confirming) confirmButton.current?.focus();
  }, [confirming]);

  const action: Action | '' = revoking ? 'Revoke' : decision;

  function validate(): string | null {
    if (!action) return 'Choose Approve or Reject.';
    if (action === 'Approve' && !effectiveDate) return 'Enter the date the exemption takes effect.';
    if (action === 'Approve' && expiresOn && expiresOn <= effectiveDate) return 'The expiry date must be after the effective date.';
    if (action === 'Reject' && !note.trim()) return 'Give a reason for rejecting the certificate.';
    if (action === 'Revoke' && !note.trim()) return 'Give a reason for revoking the approval.';
    if (note.length > MAX_NOTE) return `The note must be ${MAX_NOTE} characters or fewer.`;
    return null;
  }

  function askToConfirm() {
    const problem = validate();
    setError(problem);
    if (!problem && action) setConfirming(action);
  }

  async function record() {
    if (!action) return;
    setSaving(true);
    setError(null);
    try {
      if (action === 'Revoke') {
        await peopleGateway.revokeReciprocityRequest(employeeId, item.id, { note: note.trim() });
        setConfirming(null);
        onChanged('Revocation recorded.');
      } else {
        const updated = await peopleGateway.reviewReciprocityRequest(employeeId, item.id, {
          decision: action,
          ...(action === 'Approve' ? { effectiveDate } : {}),
          ...(action === 'Approve' && expiresOn ? { expiresOn } : {}),
          ...(note.trim() ? { note: note.trim() } : {}),
        });
        setConfirming(null);
        onChanged(action === 'Approve' ? 'Approval recorded.' : 'Rejection recorded.', updated);
      }
    } catch (err) {
      setConfirming(null);
      setError(err instanceof Error && err.message ? err.message : 'The decision could not be saved. Please try again.');
    } finally {
      setSaving(false);
    }
  }

  const pair = `${stateName(item.residenceState)} resident working in ${stateName(item.workState)}`;
  const label = chipLabel(item);
  const chipText =
    effective === 'Expired'
      ? `Expired ${formatDate(item.expiresOn)}`
      : item.status === 'Approved' && item.effectiveFrom
        ? `Approved. Effective ${formatDate(item.effectiveFrom)}`
        : label;
  const chipClass = effective === 'Expired' ? 'Expired' : item.status;
  const reviewed = !pending && item.status !== 'Revoked' && item.reviewedAtUtc;

  return (
    <article className="recip-review__card" aria-label={`${item.certificateForm} request, ${label}`}>
      <header className="recip-review__head">
        <h4>
          {item.certificateForm}: {pair}
        </h4>
        <span className={`recip-review__chip recip-review__chip--${chipClass}`}>{chipText}</span>
      </header>
      <p className="muted">
        Submitted {formatDate(item.submittedAtUtc)}. Certificate: {item.originalFileName} ({formatSize(item.sizeBytes)}){' '}
        <a href={peopleGateway.reciprocityCertificateUrl(employeeId, item.id)} target="_blank" rel="noopener noreferrer">
          View certificate
        </a>
      </p>
      {item.isStale && (
        <p className="recip-review__warning" role="alert">
          The employee&apos;s work or residence state changed after this was submitted, so it can no longer be approved. Ask the employee to submit a new
          certificate.
        </p>
      )}
      {item.status === 'Approved' && (
        <p className="muted" data-testid="recip-effective">
          {effective === 'Expired'
            ? `This approval expired on ${formatDate(item.expiresOn)}. It no longer applies. The employee can submit a new certificate to renew it.`
            : effective === 'EffectiveInFuture'
              ? `This approval starts on ${formatDate(item.effectiveFrom)}.`
              : 'This approval is in effect.'}{' '}
          {effective === 'Expired' ? '' : item.expiresOn ? `It expires ${formatDate(item.expiresOn)}.` : 'No expiry date is recorded.'}
        </p>
      )}
      {reviewed && (
        <p className="muted">
          Reviewed {formatDate(item.reviewedAtUtc)}.{item.reviewNote ? ` Note: ${item.reviewNote}` : ''}
        </p>
      )}
      {item.status === 'Revoked' && (
        <p className="muted" data-testid="recip-revoked">
          Revoked {formatDate(item.revokedAtUtc)}.{item.revokeNote ? ` Reason: ${item.revokeNote}` : ''} The certificate stays on file. The employee can submit a
          new one.
        </p>
      )}

      {canDecide && (
        <div className="recip-review__form">
          <fieldset className="recip-review__decision">
            <legend>Decision *</legend>
            <label>
              <input type="radio" name={`decision-${item.id}`} checked={decision === 'Approve'} onChange={() => { setDecision('Approve'); setError(null); }} /> Approve
            </label>
            <label>
              <input type="radio" name={`decision-${item.id}`} checked={decision === 'Reject'} onChange={() => { setDecision('Reject'); setError(null); }} /> Reject
            </label>
          </fieldset>
          {decision === 'Approve' && (
            <>
              <label className="recip-review__field">
                Effective date *
                <input type="date" value={effectiveDate} onChange={(e) => { setEffectiveDate(e.target.value); setError(null); }} />
              </label>
              <label className="recip-review__field">
                Expiry date (optional)
                <input type="date" value={expiresOn} onChange={(e) => { setExpiresOn(e.target.value); setError(null); }} />
                <span className="recip-review__hint">The last day the approval is in effect. Leave it empty if the certificate does not expire. It must be after the effective date.</span>
              </label>
            </>
          )}
          <label className="recip-review__field">
            Note {decision === 'Reject' ? '*' : '(optional)'}
            <textarea
              rows={3}
              maxLength={MAX_NOTE}
              value={note}
              onChange={(e) => { setNote(e.target.value); setError(null); }}
              placeholder={decision === 'Reject' ? 'Why the certificate is not accepted' : 'Anything the next reviewer should know'}
            />
          </label>
          {error && (
            <p className="error" role="alert">
              {error}
            </p>
          )}
          <div>
            <button type="button" onClick={askToConfirm} disabled={saving}>
              Record decision
            </button>
          </div>
        </div>
      )}

      {canRevoke && !revoking && (
        <div>
          <button type="button" onClick={() => { setRevoking(true); setNote(''); setError(null); }}>
            Revoke approval
          </button>
        </div>
      )}
      {canRevoke && revoking && (
        <div className="recip-review__form">
          <label className="recip-review__field">
            Reason for revoking *
            <textarea
              rows={3}
              maxLength={MAX_NOTE}
              value={note}
              onChange={(e) => { setNote(e.target.value); setError(null); }}
              placeholder="Why the approval no longer applies"
            />
          </label>
          {error && (
            <p className="error" role="alert">
              {error}
            </p>
          )}
          <div className="recip-review__dialog-actions">
            <button type="button" onClick={() => { setRevoking(false); setNote(''); setError(null); }} disabled={saving}>
              Cancel
            </button>
            <button type="button" onClick={askToConfirm} disabled={saving}>
              Record revocation
            </button>
          </div>
        </div>
      )}

      {confirming && (
        <div className="recip-review__scrim" onKeyDown={(e) => { if (e.key === 'Escape') setConfirming(null); }}>
          <div className="recip-review__dialog" role="alertdialog" aria-modal="true" aria-labelledby={`recip-confirm-${item.id}`} aria-describedby={`recip-confirm-desc-${item.id}`}>
            <h3 id={`recip-confirm-${item.id}`}>
              {confirming} the {item.certificateForm} {confirming === 'Revoke' ? 'approval' : 'certificate'}?
            </h3>
            <p id={`recip-confirm-desc-${item.id}`} className="muted">
              {confirming === 'Approve'
                ? `This records that you verified the certificate, effective ${formatDate(effectiveDate)}${expiresOn ? ` and expiring ${formatDate(expiresOn)}` : ', with no expiry date'}. It does not change the employee's withholding yet: the tax engine does not use it until that integration is tested.`
                : confirming === 'Reject'
                  ? 'The employee is told the certificate was not accepted and may submit a new one.'
                  : 'This ends the approval. The certificate stays on file, nothing is deleted, and the employee may submit a new certificate. It changes no withholding.'}
            </p>
            <div className="recip-review__dialog-actions">
              <button type="button" onClick={() => setConfirming(null)} disabled={saving}>
                Cancel
              </button>
              <button type="button" ref={confirmButton} onClick={() => void record()} disabled={saving}>
                {saving ? 'Saving…' : confirming}
              </button>
            </div>
          </div>
        </div>
      )}
    </article>
  );
}

/**
 * Reciprocity Exemption review (EPN-216 Step 2). An employee's upload is only a request; an authorised reviewer verifies the certificate here.
 * Approval records the decision, an effective date and an optional expiry date for the SAME state pair; a reviewer may later revoke it with a note.
 * None of it changes withholding (no tax engine integration exists yet). Earlier requests stay in the list as history and certificates are never deleted.
 * Certificate content and file names are never logged. PERMISSION DECISION REQUIRED: employees.read / employees.write for this staging slice.
 * `onChanged` runs after a decision or a revocation, so the page can read the tax setup status again.
 */
export function ReciprocityReviewSection({ employeeId, onChanged }: { employeeId: string; onChanged?: () => void }) {
  const [items, setItems] = useState<ReciprocityRequestAdminItem[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [status, setStatus] = useState<string | null>(null);

  const load = useCallback(() => {
    peopleGateway
      .listReciprocityRequests(employeeId)
      .then(setItems)
      .catch(() => setLoadError('Could not load the reciprocity requests.'));
  }, [employeeId]);

  useEffect(() => {
    load();
  }, [load]);

  function retry() {
    setLoadError(null);
    setItems(null);
    load();
  }

  return (
    <section className="profile-section recip-review" aria-labelledby="recip-review-title">
      <h3 id="recip-review-title" className="profile-section-title">
        Reciprocity Exemption Review
      </h3>
      <p className="muted">
        An employee&apos;s upload is a request, not an exemption. Verify the certificate and the state pair, then approve or reject. Approval records your
        decision, an effective date and an optional expiry date only: it does not change withholding until the tax engine integration is tested. What is
        reciprocity? <TaxTermHelp term="reciprocity" />
      </p>
      {loadError ? (
        <>
          <p className="error" role="alert">
            {loadError}
          </p>
          <button type="button" onClick={retry}>
            Retry
          </button>
        </>
      ) : items === null ? (
        <p className="muted" role="status">
          Loading reciprocity requests…
        </p>
      ) : items.length === 0 ? (
        <p className="muted" data-testid="recip-empty">
          No reciprocity requests. The employee has not submitted a certificate.
        </p>
      ) : (
        <>
          <p className="muted" data-testid="recip-history-note">
            Newest first. Earlier requests stay here as history, and a certificate is never deleted.
          </p>
          <div className="recip-review__list">
            {items.map((item) => (
              <RequestCard
                key={item.id}
                employeeId={employeeId}
                item={item}
                onChanged={(message, updated) => {
                  setStatus(message);
                  if (updated) setItems((prev) => (prev ? prev.map((p) => (p.id === updated.id ? updated : p)) : prev));
                  else load();
                  onChanged?.();
                }}
              />
            ))}
          </div>
        </>
      )}
      {status && (
        <p className="muted" role="status">
          {status}
        </p>
      )}
    </section>
  );
}
